import type { CommandSourceIo } from '../commandSources/CommandSource.js';
import { SQUAD_DEFS } from '../data/squads.js';
import type { PlayerView, Unsubscribe } from '../matchHost/MatchHost.js';
import { TICK_MS } from '../simulation/State.js';
import { makeAttackMoveCommand } from '../simulation/commands/AttackMoveCommand.js';
import type { Command } from '../simulation/commands/Command.js';
import { makeMoveCommand } from '../simulation/commands/MoveCommand.js';
import { makePickDoctrineCommand } from '../simulation/commands/PickDoctrineCommand.js';
import { makeProduceSquadCommand } from '../simulation/commands/ProduceSquadCommand.js';
import type { Core } from '../simulation/entities/Core.js';
import type { Squad, SquadArchetype } from '../simulation/entities/Squad.js';
import type { EntityId, PlayerId, Tick } from '../simulation/ids.js';
import { otherPlayer, tickOf } from '../simulation/ids.js';

import type { DesiredOrder } from './botShared.js';
import { findCoreOf, nearestNeutralRelay, nudgeToward, ordersMatch } from './botShared.js';

const DEFAULT_REACTION_DELAY_MS = 800;
// AI ticks at 5 Hz: every fourth sim tick (50 ms × 4 = 200 ms). Squad-level decisions
// don't need 20 Hz; cheaper for the bot and matches docs/07_BOT_AI.md.
const AI_TICK_INTERVAL_SIM_TICKS = 4;
const RECRUIT_DESIRED_SQUAD_COUNT = 3;
// V1.8 — Reduced from 90s to 30s. CrazyGames analytics show avg playtime ~2m34s
// with players bouncing during a long boring warmup. First combat at t=45s
// keeps the player engaged before they decide to leave.
const RECRUIT_PUSH_WARMUP_SEC = 30;
// V1.6 — Recruit only pushes when it has a CLEAR squad advantage (2+ more
// units than the enemy), not just any numeric edge. Combined with the warmup
// above, this means Recruit defaults to capturing relays during the early
// game even after the camps that used to soak up its tempo were removed.
const RECRUIT_PUSH_ADVANTAGE = 2;
const DEFEND_HP_THRESHOLD_RATIO = 0.6;
// Nudge defending squads this far from Core toward map-center so they form an
// intercepting arc rather than stacking on top of the Core sprite.
const DEFEND_ANCHOR_OFFSET_PX = 90;
const MAP_MIDPOINT = { x: 1000, y: 1000 } as const;

export interface BotPlayerOptions {
  playerId: PlayerId;
  reactionDelayMs?: number;
  desiredSquadCount?: number;
  defendHpThresholdRatio?: number;
  // Seconds of match time during which the bot will not push enemy Cores or
  // build new squads. Defaults to RECRUIT_PUSH_WARMUP_SEC. Tests that exercise
  // production / push behavior pass 0 to disable the warmup.
  pushWarmupSec?: number;
}

// Recruit-tier bot for V0.3. Reads delayed PlayerView snapshots, emits Commands.
// Pure TS — no Phaser, no DOM, no direct state mutation (Rules 6, 7).
//
// Decision pipeline each AI tick (priority order):
//   1. Defend — own Core hp < threshold → all squads recall to a defensive anchor.
//   2. Push — we have ≥ desiredSquadCount squads AND outnumber the enemy →
//      attack-move at enemy Core.
//   3. Capture — otherwise route each squad to the nearest neutral relay.
//   4. Produce — keep total squads (alive + queued + in-flight) at desiredSquadCount,
//      biased toward Assault when affordable, falling back to Recon.
//
// Reaction delay is implemented as a ring buffer of recent snapshots; tactical
// decisions read the snapshot from `reactionDelayMs` ago. This is a behavioral
// nerf (the bot reacts slower to new info) — not a cheat — per docs/07_BOT_AI.md.
export class BotPlayer {
  readonly playerId: PlayerId;
  private readonly reactionDelayTicks: number;
  private readonly desiredSquadCount: number;
  private readonly defendHpThresholdRatio: number;
  private readonly pushWarmupSec: number;
  private buffer: PlayerView[] = [];
  private emit: ((cmd: Command) => void) | null = null;
  private unsubSnapshot: Unsubscribe | null = null;
  private aiTickCounter = 0;
  private orderCache = new Map<EntityId, DesiredOrder>();
  // Delivery ticks of produce_squad commands we've sent that the delayed snapshot
  // hasn't observed yet. Used to avoid stacking the queue past desiredSquadCount.
  private inFlightProduceDeliveryTicks: number[] = [];

  constructor(opts: BotPlayerOptions) {
    this.playerId = opts.playerId;
    this.reactionDelayTicks = Math.max(
      0,
      Math.round((opts.reactionDelayMs ?? DEFAULT_REACTION_DELAY_MS) / TICK_MS),
    );
    this.desiredSquadCount = opts.desiredSquadCount ?? RECRUIT_DESIRED_SQUAD_COUNT;
    this.defendHpThresholdRatio = opts.defendHpThresholdRatio ?? DEFEND_HP_THRESHOLD_RATIO;
    this.pushWarmupSec = opts.pushWarmupSec ?? RECRUIT_PUSH_WARMUP_SEC;
  }

  start(io: CommandSourceIo): void {
    this.emit = io.emit;
    this.unsubSnapshot = io.onSnapshot((snap) => this.onSnapshot(snap));
  }

  stop(): void {
    this.unsubSnapshot?.();
    this.unsubSnapshot = null;
    this.emit = null;
    this.buffer = [];
    this.orderCache.clear();
    this.inFlightProduceDeliveryTicks = [];
    this.aiTickCounter = 0;
  }

  // Test seam: deterministic single-step decision against a hand-built snapshot.
  decideForTest(snap: PlayerView): void {
    this.buffer = [snap];
    this.aiTickCounter = AI_TICK_INTERVAL_SIM_TICKS;
    this.onSnapshot(snap);
  }

  private onSnapshot(snap: PlayerView): void {
    this.buffer.push(snap);
    const maxSize = this.reactionDelayTicks + AI_TICK_INTERVAL_SIM_TICKS + 2;
    while (this.buffer.length > maxSize) this.buffer.shift();

    this.aiTickCounter += 1;
    if (this.aiTickCounter < AI_TICK_INTERVAL_SIM_TICKS) return;
    this.aiTickCounter = 0;

    this.decide();
  }

  private decide(): void {
    if (this.emit === null || this.buffer.length === 0) return;

    const liveSnap = this.buffer[this.buffer.length - 1]!;
    if (liveSnap.outcome !== null) return;

    const delayedIdx = Math.max(0, this.buffer.length - 1 - this.reactionDelayTicks);
    const delayed = this.buffer[delayedIdx]!;

    this.inFlightProduceDeliveryTicks = this.inFlightProduceDeliveryTicks.filter(
      (deliveryTick) => deliveryTick > delayed.tick,
    );
    for (const id of [...this.orderCache.keys()]) {
      if (delayed.squads[id] === undefined) this.orderCache.delete(id);
    }

    const ownSquads: Squad[] = [];
    for (const sq of Object.values(delayed.squads)) {
      if (sq.ownerId === this.playerId) ownSquads.push(sq);
    }
    const ownCore = findCoreOf(delayed, this.playerId);
    const enemyCore = findCoreOf(delayed, otherPlayer(this.playerId));
    const intent = this.chooseIntent(delayed, ownSquads, ownCore, enemyCore);

    for (const sq of ownSquads) {
      const desired = this.orderForSquad(intent, sq, delayed, ownCore, enemyCore);
      if (desired !== null) this.emitOrderIfNew(sq, desired, liveSnap.tick);
    }

    this.maybeEmitProduce(delayed, liveSnap.tick);
    this.maybeEmitDoctrinePick(delayed, liveSnap.tick);
  }

  // Recruit's doctrine pick is deterministic (Rule 8 — no Math.random in shared)
  // and cheap (Rule 6 — same PickDoctrineCommand a human would emit). If we miss
  // the window, DoctrineSystem auto-assigns 'vanguard' on close anyway.
  private maybeEmitDoctrinePick(snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    if (!snap.doctrinePickWindowOpen) return;
    if (snap.players[this.playerId].doctrineId !== null) return;
    this.emit(
      makePickDoctrineCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        doctrineId: 'vanguard',
      }),
    );
  }

  private chooseIntent(
    snap: PlayerView,
    ownSquads: Squad[],
    ownCore: Core | null,
    enemyCore: Core | null,
  ): 'defend' | 'push' | 'capture' {
    if (ownCore !== null && ownCore.maxHp > 0 && ownCore.hp / ownCore.maxHp < this.defendHpThresholdRatio) {
      return 'defend';
    }
    const enemyCount = snap.players[otherPlayer(this.playerId)].squadCount;
    const pastWarmup = snap.matchSeconds >= this.pushWarmupSec;
    if (
      pastWarmup &&
      enemyCore !== null &&
      ownSquads.length >= this.desiredSquadCount &&
      ownSquads.length >= enemyCount + RECRUIT_PUSH_ADVANTAGE
    ) {
      return 'push';
    }
    return 'capture';
  }

  private orderForSquad(
    intent: 'defend' | 'push' | 'capture',
    squad: Squad,
    snap: PlayerView,
    ownCore: Core | null,
    enemyCore: Core | null,
  ): DesiredOrder | null {
    if (intent === 'defend') {
      if (ownCore === null) return null;
      const anchor = nudgeToward(
        { x: ownCore.pos.x, y: ownCore.pos.y },
        MAP_MIDPOINT,
        DEFEND_ANCHOR_OFFSET_PX,
      );
      return { kind: 'move', target: anchor };
    }
    if (intent === 'push' && enemyCore !== null) {
      return { kind: 'attack_move', target: { x: enemyCore.pos.x, y: enemyCore.pos.y } };
    }
    // V1.6 — during warmup the bot only targets neutral relays on its OWN
    // half (closer to ownCore than to enemyCore). This prevents the bot
    // from chasing its capture loop across the midline and stumbling into
    // the new player while they're still on the tutorial. After warmup,
    // any neutral relay is fair game.
    const pastWarmup = snap.matchSeconds >= this.pushWarmupSec;
    const relayTarget = pastWarmup || ownCore === null || enemyCore === null
      ? nearestNeutralRelay(snap, squad.pos)
      : nearestNeutralRelayOnOwnHalf(snap, squad.pos, ownCore, enemyCore);
    if (relayTarget !== null) {
      return { kind: 'move', target: relayTarget };
    }
    // No relay available — fallback. During warmup, hold near home; after
    // warmup, attack-move at the enemy Core.
    if (!pastWarmup) {
      if (ownCore === null) return null;
      const anchor = nudgeToward(
        { x: ownCore.pos.x, y: ownCore.pos.y },
        MAP_MIDPOINT,
        DEFEND_ANCHOR_OFFSET_PX,
      );
      return { kind: 'move', target: anchor };
    }
    if (enemyCore !== null) {
      return { kind: 'attack_move', target: { x: enemyCore.pos.x, y: enemyCore.pos.y } };
    }
    return null;
  }

  private emitOrderIfNew(squad: Squad, desired: DesiredOrder, liveTick: Tick): void {
    if (this.emit === null) return;
    const cached = this.orderCache.get(squad.id);
    if (cached !== undefined && ordersMatch(cached, desired)) return;
    const cmdTick = tickOf(liveTick + 1);
    if (desired.kind === 'move') {
      this.emit(
        makeMoveCommand({
          issuedBy: this.playerId,
          tick: cmdTick,
          squadIds: [squad.id],
          target: desired.target,
        }),
      );
    } else {
      this.emit(
        makeAttackMoveCommand({
          issuedBy: this.playerId,
          tick: cmdTick,
          squadIds: [squad.id],
          target: desired.target,
        }),
      );
    }
    this.orderCache.set(squad.id, { kind: desired.kind, target: { ...desired.target } });
  }

  private maybeEmitProduce(snap: PlayerView, liveTick: Tick): void {
    if (this.emit === null) return;
    const ownCore = findCoreOf(snap, this.playerId);
    if (ownCore === null || ownCore.produces === null) return;
    const player = snap.players[this.playerId];
    const queueLength = ownCore.produces.queue.length;
    const inFlight = this.inFlightProduceDeliveryTicks.length;
    const projected = player.squadCount + queueLength + inFlight;
    // V1.6 — Recruit doesn't build ANY new squads during the warmup window.
    // It still has the pre-spawned Recon scouting/capturing a near relay,
    // but the human gets a full ~90s to finish the tutorial without
    // additional bot units showing up. After warmup, normal cap of
    // desiredSquadCount applies.
    const pastWarmup = snap.matchSeconds >= this.pushWarmupSec;
    if (!pastWarmup) return;
    if (projected >= this.desiredSquadCount) return;

    const archetype: SquadArchetype = player.signal >= SQUAD_DEFS.assault.cost ? 'assault' : 'recon';
    if (player.signal < SQUAD_DEFS[archetype].cost) return;

    this.emit(
      makeProduceSquadCommand({
        issuedBy: this.playerId,
        tick: tickOf(liveTick + 1),
        producerId: ownCore.id,
        archetype,
      }),
    );
    this.inFlightProduceDeliveryTicks.push(liveTick + 1);
  }
}

// Picks the closest neutral relay that is at least as close to `ownCore` as it
// is to `enemyCore` (i.e. a relay on the bot's half of the map). Returns null
// if every neutral relay is on the enemy's half — caller falls back to
// holding position.
function nearestNeutralRelayOnOwnHalf(
  snap: PlayerView,
  from: { x: number; y: number },
  ownCore: Core,
  enemyCore: Core,
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestSq = Infinity;
  for (const node of Object.values(snap.controlNodes)) {
    if (node.ownerId !== null) continue;
    const dxOwn = node.pos.x - ownCore.pos.x;
    const dyOwn = node.pos.y - ownCore.pos.y;
    const dxEnemy = node.pos.x - enemyCore.pos.x;
    const dyEnemy = node.pos.y - enemyCore.pos.y;
    if (dxOwn * dxOwn + dyOwn * dyOwn > dxEnemy * dxEnemy + dyEnemy * dyEnemy) continue;
    const dx = node.pos.x - from.x;
    const dy = node.pos.y - from.y;
    const dSq = dx * dx + dy * dy;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { x: node.pos.x, y: node.pos.y };
    }
  }
  return best;
}

